import LocationSetter from 'components/AlertCreator/components/LocationSetter';
import Cookies from 'js-cookie';
import React, { useEffect, useState, useContext } from 'react';
import { LayoutSizeContext, UniqueWidgetRefreshContext } from 'App';
import { Circle, Popup } from 'react-leaflet';
import WidgetTimeUpdate from 'components/Widgets/WidgetTimeUpdate';
import WidgetLoadingAnimation from 'components/Widgets/WidgetLoadingAnimation';
import { ErrorMessage } from 'components/Widgets/ErrorMessage';
import { useAPIData } from 'hooks/useAPIData';
import FullScreenMap from 'components/FullScreenMap';
import useDataRefresh from 'hooks/useDataRefresh';
import { getEarthquake } from 'services/weather/weather.service';

let dataCache;

const EarthquakeMap = ({
  widget,
  widgetRefresh,
  setWidgetRefresh,
  dimensions,
  preview,
  sidebarOpen,
}) => {
  const [fetching, setFetching] = useState(true);
  const [error, setError] = useState({ state: false, message: '' });
  const layout = useContext(LayoutSizeContext);
  const FusionData = useAPIData({
    successCallback: () => {
      setFetching(false);
    },
    errorCallback: () => {
      setError({ state: true, message: 'Error fetching data' });
      setFetching(false);
    },
    fetchingCallback: () => {
      setFetching(true);
    },
  });

  const ROUTE = 'getEarthQuake';

  const [earthquakeData, setEarthquakeData] = useState([]);
  const [mapCenter, setMapCenter] = useState({
    lat: Cookies.get('latitude'),
    lng: Cookies.get('longitude'),
  });

  const [currentItem, setCurrentItem] = useState(null);
  useEffect(() => {
    dataCache = null;
    setFetching(true);
  }, [widgetRefresh]);
  const dataRefresh = useDataRefresh();
  useEffect(() => {
    (async () => {
      if (dataCache) {
        setEarthquakeData(dataCache);
        setFetching(false);
        return;
      }

      //get earthquake data
      FusionData.forceGet({ key: 'getEarthquake', apiFunction: getEarthquake })
        .then((res) => {
          dataCache = res.data.result;
          setEarthquakeData(dataCache);

          setFetching(false);
        })
        .catch((err) => {
          setError({ state: true, message: err.message });
        });
    })();
  }, [dataRefresh.widgetRefresh]);

  const uwf = useContext(UniqueWidgetRefreshContext);
  const LOCATION = Cookies.get('city') + '-' + Cookies.get('state');
  const UNIQUEKEY = LOCATION + '-' + ROUTE;

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await FusionData.forceGet({
          key: 'getEarthquake',
          apiFunction: getEarthquake,
        });
        dataCache = res.data.result;
        setEarthquakeData(dataCache);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setFetching(false);
      }
    };

    fetchData();
  }, [uwf[UNIQUEKEY]]);

  useEffect(() => {
    if (!Array.isArray(earthquakeData) || earthquakeData.length === 0) {
      setCurrentItem(null);
      return;
    }

    const hasCurrent =
      currentItem &&
      earthquakeData.some(
        (item) =>
          item?.latitude === currentItem?.latitude &&
          item?.longitude === currentItem?.longitude &&
          item?.occurrence === currentItem?.occurrence,
      );

    if (!hasCurrent) {
      const firstValid = earthquakeData.find((item) => item?.latitude && item?.longitude);
      if (firstValid) {
        setCurrentItem(firstValid);
      }
    }
  }, [earthquakeData, currentItem]);

  const capitalizeBetweenKmAndOf = (str) => {
    const start = str.indexOf('Km') + 2;
    const end = str.indexOf('Of');
    const toCapitalize = str.slice(start, end).trim();
    const capitalized = toCapitalize.toUpperCase();
    return str.slice(0, start) + ' ' + capitalized + ' ' + str.slice(end);
  };

  if (error.state && !fetching) {
    return <ErrorMessage message={error.message} />;
  }

  if (fetching && widget) {
    // if (true && widget) {
    return <WidgetLoadingAnimation widgetName={'Earthquake'} dimensions={dimensions} />;
  }

  if (fetching) {
    return <WidgetLoadingAnimation />;
  }

  if (earthquakeData && earthquakeData.length === 0) {
    return (
      <div>
        {widget && <WidgetTimeUpdate route={ROUTE} realtime />}
        <div className="p-3">No data found.</div>
      </div>
    );
  }

  const selectedItem =
    currentItem || earthquakeData?.find((item) => item?.latitude && item?.longitude);
  const panelContent = selectedItem ? (
    <div className="d-flex flex-column gap-1">
      <div
        className="fw-semibold"
        style={{ fontSize: '1rem', lineHeight: 1.2, overflowWrap: 'anywhere' }}
      >
        {capitalizeBetweenKmAndOf(selectedItem.city)}
      </div>
      <div style={{ fontSize: '0.9rem', lineHeight: 1.2 }}>
        ({selectedItem.latitude}, {selectedItem.longitude})
      </div>
      <div style={{ fontSize: '0.9rem', lineHeight: 1.2 }}>
        <span className="fw-semibold">Time:</span> {selectedItem.occurrence.slice(0, -5)}
      </div>
      <div style={{ fontSize: '0.9rem', lineHeight: 1.2 }}>
        <span className="fw-semibold">Magnitude:</span> {selectedItem.magnitude}
      </div>
      <div style={{ fontSize: '0.9rem', lineHeight: 1.2 }}>
        <span className="fw-semibold">Depth:</span> {selectedItem.depth} KM
      </div>
    </div>
  ) : null;

  const renderEarthquakeCircles = () =>
    earthquakeData.length &&
    earthquakeData.map((item, index) => {
      if (!item.latitude || !item.longitude) return null;
      return (
        <Circle
          key={index}
          center={{ lat: item.latitude, lng: item.longitude }}
          fillColor="#d1af15"
          color="red"
          weight={2}
          radius={item.magnitude * 10000 * 2.5}
          eventHandlers={{
            click: () => {
              setCurrentItem(item);
            },
          }}
        >
          <Popup>
            {item.city} ({item.latitude}, {item.longitude})<br />
            {`Magnitude: ${item.magnitude} (Depth: ${item.depth} KM)`}
            <br />
            {item.occurrence}
          </Popup>
        </Circle>
      );
    });

  const w = dimensions?.[0] || 0;
  const h = dimensions?.[1] || 0;
  const isWidget1x1 = w === 1 && h === 1;
  const shouldUseSidePanel = (h === 1 && w >= 2) || (h >= 2 && w >= 3);
  const shouldUseBottomPanel = h >= 2 && !shouldUseSidePanel;
  const scaleMult = layout?.scaleMult || 1;
  const bottomPanelMinHeight = Math.round(Math.max(108, Math.min(180, 118 * scaleMult)));
  const bottomPanelPreferredHeight = Math.round(Math.max(120, Math.min(210, 138 * scaleMult)));

  if (!widget || !dimensions)
    return (
      <div className={`h-100 min-h-sm w-100 d-flex flex-column user-select-none`}>
        <WidgetTimeUpdate route={ROUTE} realtime addMargin />
        <FullScreenMap mapCenter={mapCenter} setView={mapCenter} zoom={5}>
          <LocationSetter center={mapCenter} zoom={5} />
          {earthquakeData.length > 0 &&
            earthquakeData.map((item, index) => {
              if (!item.latitude || !item.longitude) return null; // Skip items without coordinates
              return (
                <Circle
                  key={index}
                  center={{ lat: item.latitude, lng: item.longitude }}
                  fillColor="#d1af15"
                  color="red"
                  weight={2}
                  radius={item.magnitude * 10000 * 2.5}
                >
                  <Popup>
                    {item.city} ({item.latitude}, {item.longitude})<br />
                    {`Magnitude: ${item.magnitude} (Depth: ${item.depth} KM)`}
                    <br />
                    {item.occurrence}
                  </Popup>
                </Circle>
              );
            })}
        </FullScreenMap>

        <div className="fs-5 fw-bold text-center w-100 mb-1 mt-2">Recent Earthquakes</div>
        <div className="pt-3 mb-3 overflow-y-auto h-50 h-lg-25">
          {earthquakeData.length > 0 &&
            earthquakeData.map((item, index) => {
              if (!item.latitude || !item.longitude) return null; // Skip items without coordinates
              return (
                <div
                  className="cursor-pointer"
                  key={index}
                  onClick={() => {
                    setMapCenter({
                      lat: item.latitude,
                      lng: item.longitude,
                    });
                  }}
                >
                  <div className="d-flex flex-column flex-lg-row border-bottom border-1 border-light my-2 justify-content-between fs-6">
                    <div className="mb-2 max-w-lg-25 mb-lg-0">
                      On {item.occurrence.slice(0, -5)}
                    </div>
                    <div className="mb-2 max-w-lg-25 mb-lg-0">
                      {capitalizeBetweenKmAndOf(item.city)} ({item.latitude}, {item.longitude})
                    </div>
                    <div className="mb-2 max-w-lg-25 mb-lg-0">
                      Magnitude: {item.magnitude} (Depth: {item.depth} KM)
                    </div>
                    {/* <div>{item.comments}</div> */}
                  </div>
                </div>
              );
            })}
        </div>
      </div>
    );

  if (shouldUseBottomPanel)
    return (
      <div className="d-flex h-100 flex-column">
        <WidgetTimeUpdate realtime route={ROUTE} />
        {earthquakeData.length && (
          <div className="d-flex h-100 flex-column overflow-hidden">
            <div className="flex-grow-1 min-h-0">
              <FullScreenMap mapCenter={mapCenter} zoom={5} setView={mapCenter} preview={preview}>
                <LocationSetter center={mapCenter} zoom={5} />
                {renderEarthquakeCircles()}
              </FullScreenMap>
            </div>
            {panelContent && (
              <div
                className="p-2 pt-1 d-flex overflow-auto flex-column"
                style={{
                  minHeight: bottomPanelMinHeight,
                  height: bottomPanelPreferredHeight,
                  maxHeight: '52%',
                  flex: '0 0 auto',
                }}
              >
                {panelContent}
              </div>
            )}
          </div>
        )}
      </div>
    );

  if (isWidget1x1)
    return (
      <div className="d-flex h-100 flex-column">
        <WidgetTimeUpdate realtime route={ROUTE} />
        {earthquakeData.length && (
          <div className="d-flex h-100 flex-row overflow-hidden">
            <FullScreenMap mapCenter={mapCenter} zoom={5} setView={mapCenter} preview={preview}>
              <LocationSetter center={mapCenter} zoom={5} />
              {renderEarthquakeCircles()}
            </FullScreenMap>
            {/* </MapContainer> */}
          </div>
        )}
      </div>
    );

  if (shouldUseSidePanel)
    return (
      <div className="d-flex h-100 flex-column overflow-hidden">
        <WidgetTimeUpdate realtime route={ROUTE} />
        {earthquakeData.length && (
          <div className="d-flex h-100 flex-row justify-content-between">
            <div
              className="overflow-auto"
              style={{ width: '38%', minWidth: 190, maxWidth: 320, padding: '6px 10px 6px 8px' }}
            >
              {panelContent && <div className="w-100 d-flex flex-column">{panelContent}</div>}
            </div>
            <div className="flex-grow-1 min-w-0">
              <FullScreenMap mapCenter={mapCenter} setView={mapCenter} zoom={5} preview={preview}>
                <LocationSetter center={mapCenter} zoom={5} />
                {renderEarthquakeCircles()}
              </FullScreenMap>
            </div>
          </div>
        )}
      </div>
    );
};

export default EarthquakeMap;
